import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../services/api';

// ── THE DAY GRID (mig 210) ───────────────────────────────────────────────────
// The paper Daily Scheduler's left two columns: a time column, SCHEDULED and ACTUALLY DONE.
// The right-hand columns are NOT new data — TODAYS TASKS is huddle_targets and WAITING LIST
// is huddle_blockers plus the owed items from mig 208, both already on /huddle/today.
//
// DRAG REUSES THE HR KANBAN PATTERN VERBATIM (hr/Recruitment.js:209,221,253,261): the payload
// is an id held in React state rather than dataTransfer, the drag id is cleared FIRST in the
// drop handler, a same-target drop is a silent no-op. Not a second drag implementation — the
// same one.
//
// setData IS STILL CALLED, and it is not a contradiction of the above. dataTransfer is not
// being used to CARRY the payload — the state still does that — but Firefox treats a
// dragstart that sets no data as a drag it should not begin, and silently refuses. Chrome,
// Edge and Safari start the drag anyway, which is why this survived review: the handler looks
// complete and fails on one engine with no console error and no visible attempt.
// Measured 2026-08-17: huddle_slots held 0 rows since the feature shipped — nobody had ever
// completed a drop. The API was fine throughout; both endpoints returned 201/200 when called
// directly. effectAllowed = 'move' is set for the same reason: it is what the drop means.
//
// NO TYPING ANYWHERE. Items arrive by drag; a slot closes through a dropdown.
const C = { navy: '#3B3A8F', muted: '#888', border: '#E8E7E7', green: '#166534', amber: '#B45309', red: '#A32D2D' };

// 09:00–18:30 on the half hour = 20; 13:00 is lunch and is not a drop target. 19 working slots.
// The DB refuses 13:00 too (huddle_slots_time_chk), so this is the readable half of one rule.
const SLOTS = (() => {
  const out = []; let h = 9, m = 0;
  while (h < 19) { out.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`); m += 30; if (m === 60) { h += 1; m = 0; } }
  return out;
})();
const LUNCH = '13:00';

const box = { background: '#fff', border: `1px solid ${C.border}`, borderRadius: 10 };
const chip = (bg, fg) => ({ background: bg, color: fg, borderRadius: 10, padding: '1px 7px', fontSize: 10, fontWeight: 700 });

// An item's REAL state, read from the item itself and never from the slot. A blocker resolved
// from the Stuck section while sitting in a 15:00 slot is struck through here and the slot is
// NOT cascaded: the grid records the plan, not the outcome of the item.
function itemOf(s) {
  if (s.target_id) return { body: s.target_body, gpo: s.target_gpo, kind: 'target', dead: s.target_outcome === 'done', deadLabel: 'target done' };
  const dead = !!s.blocker_resolved_at;
  return { body: s.blocker_body, gpo: s.blocker_gpo, kind: 'stuck', dead,
    deadLabel: dead ? 'resolved elsewhere' : s.blocker_cleared_at ? 'cleared — awaiting confirm' : null,
    owed: s.blocker_owed_by_name, raisedBy: s.blocker_raised_by_name };
}

function SlotCard({ s, readOnly, onDrag, onOutcome, onDelete, selected, onSelect, anySelected }) {
  const it = itemOf(s);
  const closed = s.outcome !== 'planned';
  return (
    <div draggable={!readOnly && !closed}
      onDragStart={e => { if (closed) return; e.dataTransfer.setData('text/plain', s.id); e.dataTransfer.effectAllowed = 'move'; onDrag(s.id); }}
      // CLICK-TO-PLACE. When something is already picked up, this click must FALL THROUGH to
      // the slot cell so it places there. Only when nothing is picked does clicking a card
      // pick it up — which is how a slotted card is selected for moving, so reschedule works
      // exactly like placing and there is one interaction to learn, not two.
      onClick={e => { if (readOnly || closed || anySelected) return; e.stopPropagation(); onSelect(s.id); }}
      style={{ ...box, borderLeft: `3px solid ${s.outcome === 'done' ? C.green : s.outcome === 'not_done' ? C.red : it.kind === 'stuck' ? C.amber : C.navy}`,
        padding: '6px 9px', marginBottom: 5,
        cursor: readOnly || closed ? 'default' : anySelected ? 'copy' : 'pointer',
        outline: selected ? `2px solid ${C.navy}` : 'none', outlineOffset: 1,
        boxShadow: selected ? '0 0 0 4px rgba(59,58,143,0.12)' : 'none',
        opacity: s.outcome === 'rescheduled' ? 0.55 : 1 }}>
      <div style={{ display: 'flex', gap: 6, alignItems: 'baseline', flexWrap: 'wrap' }}>
        {it.gpo && <strong style={{ fontFamily: 'monospace', color: C.red, fontSize: 11 }}>{it.gpo}</strong>}
        <span style={{ fontSize: 12, flex: 1,
          textDecoration: (it.dead || s.outcome === 'rescheduled') ? 'line-through' : 'none',
          color: (it.dead || s.outcome === 'rescheduled') ? C.muted : '#222' }}>
          {it.body || <em style={{ color: C.muted }}>(GPO only)</em>}
        </span>
        {it.deadLabel && <span style={chip('#F3F4F6', C.muted)}>{it.deadLabel}</span>}
        {s.outcome === 'rescheduled' && <span style={chip('#F3F4F6', C.muted)}>moved</span>}
        {s.outcome === 'done' && <span style={chip('#EAF7EE', C.green)}>done{s.done_at ? ` ${String(s.done_at).slice(0, 5)}` : ''}</span>}
        {s.outcome === 'not_done' && <span style={chip('#FCEBEB', C.red)}>not done</span>}
      </div>
      {it.owed && <div style={{ fontSize: 10, color: C.muted, marginTop: 2 }}>waiting on {it.owed}</div>}
      {it.raisedBy && !it.owed && <div style={{ fontSize: 10, color: C.muted, marginTop: 2 }}>raised by {it.raisedBy}</div>}
      {s.outcome_note && <div style={{ fontSize: 10, color: C.muted, marginTop: 2 }}>{s.outcome_note}</div>}
      {/* The outcome control now lives in the ACTUAL column, where the paper form puts it.
          Only the remove stays on the card. */}
      {!readOnly && !closed && (
        <div style={{ display: 'flex', gap: 6, marginTop: 5 }} onClick={e => e.stopPropagation()}>
          <button onClick={() => onDelete(s)} title="Remove from the grid"
            style={{ border: 'none', background: 'none', color: C.muted, cursor: 'pointer', fontSize: 12 }}>✕ remove</button>
        </div>
      )}
    </div>
  );
}

// MODULE SCOPE, DELIBERATELY — DO NOT MOVE THIS BACK INSIDE HuddleGrid.
// It was declared inside the component body, so every HuddleGrid render produced a NEW
// function identity. React compares element types by reference, could not reconcile it as
// the same component, and unmounted + remounted the whole subtree — destroying the real
// <input> DOM node underneath "Add work that came up today…". Typing set newBody, which
// re-rendered the parent, which remounted the tray, which threw away focus after exactly
// one character. The value survived (parent state) so it read as "it eats my typing".
// The seven props below are what it used to reach through the closure; that is the whole
// cost of keeping the type stable.
// A tray is a COLUMN that scrolls inside itself. The rail is full height and the two trays
// share it, so a long Waiting List scrolls against a grid that stays put — with 10 targets
// and 10 blockers the old content-height rail pushed the page down and the 18:30 slot ended
// up below the fold, which is a poor place to keep a drop target.
const Tray = ({ title, items, empty, readOnly, newBody, setNewBody, addNew, sel, setSel, setDrag }) => {
  // Done LAST and dimmed, not hidden. Recording that something happened is the ordinary
  // evening case, so a finished target must stay draggable into the slot it happened in —
  // the grid records when work occurred, not only what was planned.
  const live = items.filter(i => !i._done);
  const done = items.filter(i => i._done);
  const ordered = [...live, ...done];
  return (
    <div style={{ ...box, padding: 10, marginBottom: 10, display: 'flex', flexDirection: 'column', minHeight: 0, flex: '1 1 0' }}>
      <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.5, color: C.navy, textTransform: 'uppercase', marginBottom: 6, flex: '0 0 auto' }}>
        {title} <span style={{ color: C.muted, fontWeight: 500 }}>· {live.length}{done.length ? ` + ${done.length} done` : ''}</span>
      </div>
      {/* ADD NEW, IN THE POOL ITSELF, and it works after the huddle locks. Work that
          arises at 15:00 is not a revision of the morning commitment — it is new work, and
          a day grid that can only schedule what someone predicted before the meeting is
          not a day grid. The row it writes is flagged added_after_lock so the register
          never blurs a mid-day arrival with a 09:50 commitment. */}
      {!readOnly && (
        <form onSubmit={e => { e.preventDefault(); addNew(); }}
          style={{ display: 'flex', gap: 5, marginBottom: 8, flex: '0 0 auto' }}>
          <input value={newBody} onChange={e => setNewBody(e.target.value)}
            placeholder="Add work that came up today…"
            style={{ flex: 1, minWidth: 0, padding: '5px 8px', border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 12 }} />
          <button type="submit" disabled={!newBody.trim()}
            style={{ padding: '5px 10px', borderRadius: 6, border: 'none', cursor: newBody.trim() ? 'pointer' : 'default',
              background: newBody.trim() ? C.navy : '#E5E7EB', color: newBody.trim() ? '#fff' : '#9CA3AF', fontSize: 12, fontWeight: 600 }}>
            + Add
          </button>
        </form>
      )}
      <div style={{ overflowY: 'auto', minHeight: 0, flex: '1 1 auto' }}>
        {/* AN EMPTY TRAY MUST READ AS EMPTY, NOT BROKEN. A bare em-dash beside a grid that
            does nothing looks like a failure; naming the reason and where to fix it is
            probably half of every "drag doesn't work" report. */}
        {ordered.length === 0 && (
          <div style={{ fontSize: 11, color: C.muted, lineHeight: 1.5 }}>{empty}</div>
        )}
        {ordered.map(i => (
          <div key={i.id} draggable={!readOnly}
            onDragStart={e => { e.dataTransfer.setData('text/plain', i.id); e.dataTransfer.effectAllowed = 'move'; setDrag({ [i._key]: i.id }); }}
            // Click to pick up; click again to put down. The same card is the cancel.
            onClick={() => { if (readOnly) return; setSel(sel && sel[i._key] === i.id ? null : { [i._key]: i.id }); }}
            style={{ ...box, borderLeft: `3px solid ${i._done ? '#CBD5E1' : i._kind === 'target' ? C.navy : C.amber}`, padding: '5px 8px', marginBottom: 5,
              cursor: readOnly ? 'default' : 'pointer', fontSize: 12, opacity: i._done ? 0.6 : 1,
              outline: sel && sel[i._key] === i.id ? `2px solid ${C.navy}` : 'none', outlineOffset: 1,
              boxShadow: sel && sel[i._key] === i.id ? '0 0 0 4px rgba(59,58,143,0.12)' : 'none' }}>
            <div style={{ display: 'flex', gap: 5, alignItems: 'baseline', flexWrap: 'wrap' }}>
              {i.gpo_no && <strong style={{ fontFamily: 'monospace', color: C.red, fontSize: 11 }}>{i.gpo_no}</strong>}
              <span style={{ flex: 1, textDecoration: i._done ? 'line-through' : 'none', color: i._done ? C.muted : 'inherit' }}>
                {i.body || <em style={{ color: C.muted }}>(GPO only)</em>}
              </span>
              {i._done && <span style={chip('#EAF7EE', C.green)}>done</span>}
              {i._kind === 'owed' && <span style={chip('#FEF3C7', C.amber)}>owed to you</span>}
              {i._kind === 'stuck' && <span style={chip('#FEF3C7', C.amber)}>stuck</span>}
              {i.added_after_lock && <span style={chip('#F3F4F6', C.muted)}>added today</span>}
              {/* One item written in two sections. Said plainly rather than shown twice. */}
              {i._alsoStuck && <span style={chip('#FEF3C7', C.amber)}>also stuck</span>}
            </div>
            {/* Style, colour, carry age and the promoted task were all already on the wire
                and shown on My Sheet — the tray was the one place that dropped them, which
                made two cards for the same GPO indistinguishable while dragging. */}
            {(i.style_no || i.colour) && (
              <div style={{ fontSize: 10, color: C.muted }}>{[i.style_no, i.colour].filter(Boolean).join(' · ')}</div>
            )}
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 2 }}>
              {i.carry_count > 0 && <span style={chip('#FEF3C7', '#8A5A00')}>carried ×{i.carry_count}</span>}
              {i.task_no && <span style={chip('#EEF0FA', C.navy)}>→ {i.task_no}{i.task_owner_name ? ` · ${i.task_owner_name}` : ''}</span>}
              {i.owed_by_name && <span style={chip('#F3F4F6', C.muted)}>waiting on {i.owed_by_name}</span>}
              {i.raised_by_name && <span style={chip('#F3F4F6', C.muted)}>from {i.raised_by_name}</span>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default function HuddleGrid({ date, targets = [], blockers = [], owed = [], entryId, userId, readOnly = false, onChanged }) {
  const [slots, setSlots] = useState([]);
  const [err, setErr] = useState('');
  // dragId holds EITHER a slot id (moving within the grid) or a pending item — the same
  // "payload is an id in state" shape the kanban uses.
  const [drag, setDrag] = useState(null);   // { slotId } | { target_id } | { blocker_id }
  // CLICK-TO-PLACE — the PRIMARY path. Same payload shape as drag, so one place() serves both.
  // Drag stays for whoever it works for; this cannot fail silently, works on touch, and shows
  // its own state, so an empty result is distinguishable from a broken one.
  const [sel, setSel] = useState(null);
  const [newBody, setNewBody] = useState('');

  const load = useCallback(() => {
    api.get('/huddle/slots', { params: { date, ...(userId ? { user_id: userId } : {}) } })
      .then(r => setSlots(r.data.data.slots || [])).catch(() => setSlots([]));
  }, [date, userId]);
  useEffect(() => { load(); }, [load]);

  // Memoised because `pool` below depends on it: a fresh Set every render would give the
  // pool memo a new dependency identity each time and it would never hit.
  const slotted = useMemo(
    () => new Set(slots.filter(s => s.outcome === 'planned').map(s => s.target_id || s.blocker_id)),
    [slots]);
  const fail = (e) => setErr(e.response?.data?.error || 'Failed');
  const after = () => { setErr(''); load(); onChanged?.(); };

  // ONE placement path, reached by drag OR by click. Whatever fixes or breaks drag, click
  // behaves identically, so the two can never diverge into different bugs.
  const place = async (d, time) => {
    if (!d || readOnly || time === LUNCH) return;
    try {
      if (d.slotId) {
        const cur = slots.find(x => x.id === d.slotId);
        if (!cur || String(cur.slot_time).slice(0, 5) === time) return;   // same slot = no-op
        await api.post(`/huddle/slots/${d.slotId}/move`, { slot_time: time });
      } else {
        await api.post('/huddle/slots', { entry_id: entryId, slot_time: time, ...d });
      }
      after();
    } catch (e) { fail(e); }
  };
  const handleDrop = (time) => { const d = drag; setDrag(null); place(d, time); };   // clear FIRST
  const handleClickSlot = (time) => { const d = sel; setSel(null); place(d, time); };
  // Escape cancels a pick-up. Without it the only way out of a mis-click is to place it
  // somewhere wrong and undo, which is exactly the kind of dead end that reads as broken.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setSel(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const onOutcome = async (s, outcome) => {
    let note = '';
    // Rescheduled asks for nothing: "it did not happen today" is the whole statement, and
    // demanding prose for an ordinary re-plan is how a control stops being used.
    if (outcome === 'not_done') {
      const t = window.prompt('Why was it not done? (required)');
      if (t === null) return;
      if (!t.trim()) { setErr('Say why it was not done — a reason is required.'); return; }
      note = t.trim();
    }
    try { await api.post(`/huddle/slots/${s.id}/outcome`, { outcome, note }); after(); } catch (e) { fail(e); }
  };
  // Creates a target on the CURRENT sheet, through the exempt pool route, so it works after
  // the lock. It lands in the pool ready to slot rather than being placed automatically —
  // adding work and scheduling it are two decisions.
  const addNew = async () => {
    const body = newBody.trim();
    if (!body || readOnly) return;
    try { await api.post('/huddle/slots/pool-target', { entry_id: entryId, body }); setNewBody(''); after(); }
    catch (e) { fail(e); }
  };

  const onDelete = async (s) => {
    try { await api.delete(`/huddle/slots/${s.id}`); after(); } catch (e) { fail(e); }
  };

  // WAITING LIST = my stuck items + what others say I owe. TODAYS TASKS = my targets.
  // Both filtered to what is not already live in a slot — an item in the grid is not also
  // sitting in the tray waiting to be dragged again.
  // Done targets STAY in the tray, flagged — see the Tray comment. Only slotted items leave.
  // ── ONE POOL, not two boxes ─────────────────────────────────────────────
  // Targets, stuck items and what others owe me, merged and DE-DUPLICATED. The same work
  // written once as a target and once as a blocker is ONE card: people routinely write the
  // same job in both sections, and two cards for one job makes the pool lie about how much
  // there is. Keyed on GPO + normalised body. The TARGET wins when both exist, because it is
  // the commitment and the blocker is the reason it has not moved — but the card says both.
  const norm = (x) => `${(x.gpo_no || '').trim().toLowerCase()}|${(x.body || '').trim().toLowerCase().replace(/\s+/g, ' ')}`;
  // MEMOISED on its four real inputs. This was a bare IIFE, so it rebuilt and re-deduplicated
  // the whole pool on every render — including every keystroke in the add box. With Tray now
  // a stable type, `pool`'s identity is what decides whether the tray's children re-render per
  // character, so leaving it unmemoised would just move the churn one level down.
  const pool = useMemo(() => {
    const seen = new Map();
    const add = (i, kind, keyName) => {
      const k = norm(i);
      const put = (v) => seen.set(k === '|' ? i.id : k, v);
      if (k === '|') { put({ ...i, _kind: kind, _key: keyName }); return; }
      const prev = seen.get(k);
      if (!prev) { put({ ...i, _kind: kind, _key: keyName }); return; }
      if (kind === 'target' && prev._kind !== 'target') put({ ...i, _kind: 'target', _key: keyName, _alsoStuck: true });
      else if (kind !== 'target' && prev._kind === 'target') put({ ...prev, _alsoStuck: true });
    };
    targets.filter(t => !slotted.has(t.id))
      .forEach(t => add({ ...t, _done: t.outcome === 'done' }, 'target', 'target_id'));
    blockers.filter(b => !b.resolved_at && !slotted.has(b.id)).forEach(b => add(b, 'stuck', 'blocker_id'));
    owed.filter(o => !slotted.has(o.id)).forEach(o => add(o, 'owed', 'blocker_id'));
    return [...seen.values()];
  }, [targets, blockers, owed, slotted]);   // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div>
      {/* CLICK IS NAMED FIRST because it is the primary path — it works in every browser and
          on touch, and it shows its own state. Drag is offered second, for whoever it works
          for. Naming drag first is how a broken drag reads as a broken feature. */}
      <div style={{ fontSize: 12, color: C.muted, marginBottom: 10 }}>
        {readOnly ? 'Read-only — this is someone else’s day.'
          : sel
            ? <span style={{ color: C.navy, fontWeight: 700 }}>Picked up — now click a slot to place it. Click the card again, or press Esc, to cancel.</span>
            : 'Click an item on the right, then click a slot to place it. Click a slotted card to move it. Dragging works too.'}
      </div>
      {err && <div style={{ color: C.red, fontSize: 12, marginBottom: 8 }}>{err}</div>}
      {/* stretch, not start: the rail must be as tall as the grid so its trays have a height
          to scroll within. alignItems:'start' collapsed them to content height, which is why a
          20-item rail simply grew the page instead of scrolling. */}
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.6fr) minmax(0,1fr)', gap: 14, alignItems: 'stretch' }}>
        <div style={{ ...box, overflow: 'hidden' }}>
          {/* PLANNED and ACTUAL as two columns, as on the paper form. They are independent:
              planned 11:00 / done 15:00 is an ordinary day, not an anomaly, and the layout
              has to make that readable rather than hide it in a chip. */}
          <div style={{ display: 'grid', gridTemplateColumns: '68px 1fr 150px', background: '#F7F6F3', borderBottom: `1px solid ${C.border}`, fontSize: 11, fontWeight: 700, color: C.muted }}>
            <div style={{ padding: '7px 9px' }}>TIME</div>
            <div style={{ padding: '7px 9px' }}>PLANNED</div>
            <div style={{ padding: '7px 9px' }}>ACTUAL</div>
          </div>
          {SLOTS.map(t => {
            const rows = slots.filter(s => String(s.slot_time).slice(0, 5) === t);
            const isLunch = t === LUNCH;
            return (
              <div key={t} style={{ display: 'grid', gridTemplateColumns: '68px 1fr 150px', borderBottom: '1px solid #F2F2F2',
                background: isLunch ? '#FAFAF7' : '#fff' }}>
                <div style={{ padding: '7px 9px', fontSize: 12, fontWeight: 600, color: isLunch ? C.muted : '#333' }}>{t}</div>
                {/* LUNCH IS FIXED AND NON-DROPPABLE — no onDrop handler at all, so it cannot
                    accept an item even by accident. The DB refuses 13:00 as well. */}
                <div onDragOver={e => { if (!isLunch && !readOnly) e.preventDefault(); }}
                  onDrop={e => { e.preventDefault(); if (!isLunch) handleDrop(t); }}
                  // The whole cell is the click target, not just the cards in it — an empty
                  // slot must be clickable or the primary path could not place anything.
                  onClick={() => { if (!isLunch && sel) handleClickSlot(t); }}
                  style={{ padding: '6px 8px', minHeight: 34,
                    cursor: sel && !isLunch && !readOnly ? 'copy' : 'default',
                    background: sel && !isLunch && !readOnly ? '#F5F7FF' : 'transparent',
                    outline: sel && !isLunch && !readOnly ? '1px dashed #C7CBEF' : 'none', outlineOffset: -2 }}>
                  {isLunch ? <span style={{ fontSize: 11, color: C.muted, fontStyle: 'italic' }}>Lunch</span>
                    : rows.map(s => <SlotCard key={s.id} s={s} readOnly={readOnly}
                        onDrag={id => setDrag({ slotId: id })} onOutcome={onOutcome} onDelete={onDelete}
                        selected={!!sel && sel.slotId === s.id} anySelected={!!sel}
                        onSelect={id => setSel({ slotId: id })} />)}
                </div>
                {/* THE ACTUAL COLUMN. One dropdown per slot — Done / Not Done / Rescheduled —
                    and the recorded outcome once closed, with the time it actually happened. */}
                <div style={{ padding: '6px 8px', borderLeft: '1px solid #F2F2F2' }} onClick={e => e.stopPropagation()}>
                  {isLunch ? null : rows.map(s => (
                    <div key={s.id} style={{ marginBottom: 5, minHeight: 26 }}>
                      {s.outcome === 'planned'
                        ? (!readOnly && (
                            <select defaultValue="" onChange={e => { const v = e.target.value; e.target.value = ''; if (v) onOutcome(s, v); }}
                              style={{ width: '100%', fontSize: 11, padding: '3px 5px', border: `1px solid ${C.border}`, borderRadius: 5 }}>
                              <option value="">—</option>
                              <option value="done">Done</option>
                              <option value="not_done">Not Done</option>
                              <option value="rescheduled">Rescheduled</option>
                            </select>))
                        : <span style={chip(
                            s.outcome === 'done' ? '#EAF7EE' : s.outcome === 'not_done' ? '#FCEBEB' : '#F3F4F6',
                            s.outcome === 'done' ? C.green : s.outcome === 'not_done' ? C.red : C.muted)}>
                            {s.outcome === 'done' ? `Done${s.done_at ? ' ' + String(s.done_at).slice(0, 5) : ''}`
                              : s.outcome === 'not_done' ? 'Not Done' : 'Rescheduled'}
                          </span>}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0, maxHeight: '78vh' }}>
          <Tray title="The pool" items={pool}
            empty="Nothing left to slot. Add work below, or write it on My Sheet."
            readOnly={readOnly} newBody={newBody} setNewBody={setNewBody} addNew={addNew}
            sel={sel} setSel={setSel} setDrag={setDrag} />
          <div style={{ fontSize: 10, color: C.muted }}>
            An unslotted task is still a commitment — the grid is structure, not a gate.
          </div>
        </div>
      </div>
    </div>
  );
}
